import { type UseMutationResult } from '@tanstack/react-query';

import { useApiMutation } from '../../../lib/api/hooks.js';
import { queryKeys } from '../../../lib/api/query-keys.js';
import { parseOrThrow } from '../../../lib/api/contract.js';
import { cancellationPolicyEndpoints } from './endpoints.js';
import {
  createdCancellationPolicy,
  type CancellationPolicyFormInput,
  type CreatedCancellationPolicy,
} from './schemas.js';

/**
 * Writes for the Cancellation Policies module.
 *
 * The shape every mutation in the portal copies:
 *
 * - **An idempotency key per submission.** The Gateway retains keys for 24
 *   hours, so a double-submitted form is one policy rather than two. Fresh per
 *   call — a stable key would make the second policy a replay of the first.
 * - **Invalidate the lists and the details**, not a hand-listed set of pages.
 *   The options key sits outside both prefixes on purpose.
 * - **The response is parsed.** A create returning an unexpected shape is the
 *   same contract failure as a read that does.
 * - **Errors are not swallowed** — `ApiError.fieldErrors` reaches the caller so
 *   the form can attach each message to the field that caused it.
 */
const WRITTEN_KEYS = [queryKeys.cancellationPolicies.lists, queryKeys.cancellationPolicies.details];

/**
 * The fields the form collects that the service treats as optional.
 *
 * One list because create and edit need opposite things from it: create
 * **omits** an empty one so the service applies its own default, while edit
 * sends `null`, which is the only way to say "clear what is there". Sending
 * `''` would do neither — it writes an empty string into a column whose
 * emptiness is spelled `null` everywhere else in the platform.
 */
const OPTIONAL_FIELDS = [
  'description',
  'contentHtml',
] as const satisfies readonly (keyof CancellationPolicyFormInput)[];

export function createCancellationPolicyPayload(
  values: CancellationPolicyFormInput,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    propertyId: values.propertyId,
    code: values.code,
    name: values.name,
    status: values.status,
  };

  for (const field of OPTIONAL_FIELDS) {
    if (values[field] !== '') payload[field] = values[field];
  }

  // `terms` is deliberately absent. Nothing evaluates it yet, so the service's
  // own default of `{}` is the honest value — see the spec, decision 2.
  return payload;
}

export function updateCancellationPolicyPayload(
  values: CancellationPolicyFormInput,
  version: number,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    name: values.name,
    status: values.status,
    // The version the editor loaded, not the one on the record now. This is
    // the whole lock: without it the server cannot tell a fresh edit from one
    // that started before someone else saved.
    version,
  };

  for (const field of OPTIONAL_FIELDS) {
    payload[field] = values[field] === '' ? null : values[field];
  }

  // `code` and `propertyId` are absent because the service will not change
  // them: the code appears in exported rate configuration, and moving a policy
  // between properties would re-scope every rate plan referencing it.
  return payload;
}

export function useCreateCancellationPolicy(): UseMutationResult<
  CreatedCancellationPolicy,
  unknown,
  CancellationPolicyFormInput
> {
  return useApiMutation<CancellationPolicyFormInput, CreatedCancellationPolicy>({
    mutationFn: async (api, input) => {
      const payload = await api.post<unknown>(
        cancellationPolicyEndpoints.create,
        createCancellationPolicyPayload(input),
        crypto.randomUUID(),
      );
      return parseOrThrow(createdCancellationPolicy, payload, cancellationPolicyEndpoints.create);
    },
    invalidate: WRITTEN_KEYS,
  });
}

export interface UpdateCancellationPolicyVariables {
  readonly policyId: string;
  readonly version: number;
  readonly values: CancellationPolicyFormInput;
}

export function useUpdateCancellationPolicy(): UseMutationResult<
  unknown,
  unknown,
  UpdateCancellationPolicyVariables
> {
  return useApiMutation<UpdateCancellationPolicyVariables, unknown>({
    mutationFn: async (api, { policyId, version, values }) => {
      // PATCH, not PUT: the service takes a partial, and a PUT would erase by
      // omission the `terms` this form does not own.
      await api.patch<unknown>(
        cancellationPolicyEndpoints.update(policyId),
        updateCancellationPolicyPayload(values, version),
      );
    },
    invalidate: WRITTEN_KEYS,
  });
}

/**
 * Deleting a cancellation policy.
 *
 * Soft, always: the service sets `deleted_at` and `deleted_by` and leaves the
 * row where it is, because rate plans still point at it. A rate plan whose
 * policy row vanished would sell with no cancellation terms at all.
 */
export function useSoftDeleteCancellationPolicy(): UseMutationResult<unknown, unknown, string> {
  return useApiMutation<string, unknown>({
    mutationFn: async (api, policyId) => {
      await api.delete<unknown>(cancellationPolicyEndpoints.delete(policyId));
    },
    invalidate: WRITTEN_KEYS,
  });
}
